import { resolveSharesContainingRanges } from 'modules/partners/api/resolveSharesContainingRanges';

import {
  EncumbranceExcelShareRef,
  EncumbranceExcelRow,
  EncumbranceExcelGroup,
} from '../types/EncumbrancesExcel';

export const findShareContainingRange = (
  shares: EncumbranceExcelShareRef[],
  shareFrom: number,
  shareTo: number
): EncumbranceExcelShareRef | null => {
  const match = shares.find(
    (share) =>
      share.isActive !== false &&
      Number(share.from) <= shareFrom &&
      Number(share.to) >= shareTo
  );

  return match || null;
};

const rangeKey = (partnerId: string, shareFrom: number, shareTo: number) =>
  `${partnerId}:${shareFrom}:${shareTo}`;

/**
 * Resolve only the share that contains each Excel range (one dedicated API call).
 * Does not fetch all shares of each partner.
 */
export const resolveSharesForEncumbranceGroups = async ({
  societyId,
  groups,
}: {
  societyId: string;
  groups: EncumbranceExcelGroup[];
}): Promise<Map<string, EncumbranceExcelShareRef>> => {
  const ranges = groups.reduce<
    { partnerId: string; shareFrom: number; shareTo: number }[]
  >((acc, group) => {
    if (!group.resolvedHolderPartnerId) {
      return acc;
    }
    if (group.errors.some((error) => error.code === 'HOLDER_NOT_FOUND')) {
      return acc;
    }

    acc.push({
      partnerId: group.resolvedHolderPartnerId,
      shareFrom: group.shareFrom,
      shareTo: group.shareTo,
    });
    return acc;
  }, []);

  if (!ranges.length) {
    return new Map();
  }

  const matches = await resolveSharesContainingRanges({ societyId, ranges });
  const byKey = new Map<string, EncumbranceExcelShareRef>();

  matches.forEach((match) => {
    if (!match.share) {
      return;
    }

    byKey.set(rangeKey(match.partnerId, match.shareFrom, match.shareTo), {
      _id: match.share._id,
      from: match.share.from,
      to: match.share.to,
      partner: match.share.partner,
      isActive: true,
    });
  });

  return byKey;
};

export const getResolvedShareForGroup = (
  sharesByRangeKey: Map<string, EncumbranceExcelShareRef>,
  holderPartnerId: string,
  shareFrom: number,
  shareTo: number
): EncumbranceExcelShareRef | null =>
  sharesByRangeKey.get(rangeKey(holderPartnerId, shareFrom, shareTo)) || null;

export const getUniqueHolderPartnerIdsFromGroups = (
  groups: {
    holderRow: EncumbranceExcelRow | null;
    resolvedHolderPartnerId?: string;
  }[]
): string[] => {
  const ids = new Set<string>();

  groups.forEach((group) => {
    if (group.resolvedHolderPartnerId) {
      ids.add(group.resolvedHolderPartnerId);
    }
  });

  return [...ids];
};
